import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { invoiceAPI, expenditureAPI } from '../../../services';
import { LoadingSpinner } from '../../UI';
import {
  C, Mono, Card, SectionCard, StatCard, SegmentedControl, GroupedBars,
  Table, TH, TD, fmtINR,
} from './theme';

/* Derived P&L: billing revenue (invoices raised) minus platform expenditures.
   Revenue is bucketed locally from a single invoice fetch; expenditure comes
   from the expenditure aggregation — monthly reuses the stats endpoint's
   six-month trend, wider granularities ask for one total per bucket. */

const GRANULARITIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'half', label: 'Half-Yearly' },
  { value: 'yearly', label: 'Yearly' },
];

const iso = (d) => d.toISOString().split('T')[0];
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

// Buckets to report on, oldest first.
const buildBuckets = (granularity) => {
  const now = new Date();
  if (granularity === 'monthly') {
    return Array.from({ length: 6 }, (_, i) => {
      const start = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
      return {
        key: monthKey(start),
        label: start.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        short: start.toLocaleDateString('en-US', { month: 'short' }),
        start,
        end,
      };
    });
  }
  if (granularity === 'half') {
    return Array.from({ length: 4 }, (_, i) => {
      const anchor = new Date(now.getFullYear(), now.getMonth() - (3 - i) * 6, 1);
      const firstHalf = anchor.getMonth() < 6;
      const start = new Date(anchor.getFullYear(), firstHalf ? 0 : 6, 1);
      const end = new Date(anchor.getFullYear(), firstHalf ? 6 : 12, 0);
      const label = `${firstHalf ? 'H1' : 'H2'} ${anchor.getFullYear()}`;
      return { key: label, label, short: label, start, end };
    });
  }
  return Array.from({ length: 3 }, (_, i) => {
    const year = now.getFullYear() - (2 - i);
    return {
      key: String(year),
      label: String(year),
      short: String(year),
      start: new Date(year, 0, 1),
      end: new Date(year, 11, 31),
    };
  });
};

const ProfitSummary = () => {
  const [granularity, setGranularity] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [spendByBucket, setSpendByBucket] = useState({});

  const buckets = useMemo(() => buildBuckets(granularity), [granularity]);

  // Invoices cover the widest window any granularity needs (three years).
  useEffect(() => {
    const start = new Date();
    start.setFullYear(start.getFullYear() - 3, 0, 1);
    invoiceAPI
      .getInvoices({ startDate: start.toISOString(), limit: 500 })
      .then((r) => setInvoices(r?.data?.invoices || []))
      .catch(() => setInvoices([]));
  }, []);

  const loadSpend = useCallback(async () => {
    setLoading(true);
    const safe = (p) => p.then((r) => r).catch(() => null);

    if (granularity === 'monthly') {
      // The stats endpoint already returns a six-month expenditure trend.
      const res = await safe(expenditureAPI.getStats());
      const map = {};
      (res?.data?.monthlyTrend || []).forEach((m) => {
        map[`${m._id.year}-${String(m._id.month).padStart(2, '0')}`] = m.total || 0;
      });
      setSpendByBucket(map);
    } else {
      const results = await Promise.all(
        buckets.map((b) => safe(expenditureAPI.getStats({ startDate: iso(b.start), endDate: iso(b.end) })))
      );
      const map = {};
      buckets.forEach((b, i) => {
        map[b.key] = results[i]?.data?.total || 0;
      });
      setSpendByBucket(map);
    }
    setLoading(false);
  }, [granularity, buckets]);

  useEffect(() => {
    loadSpend();
  }, [loadSpend]);

  const rows = useMemo(
    () =>
      buckets.map((b) => {
        const revenue = invoices
          .filter((inv) => {
            if (inv.status === 'cancelled') return false;
            const d = new Date(inv.issueDate);
            return d >= b.start && d <= b.end;
          })
          .reduce((s, inv) => s + (Number(inv.total) || 0), 0);
        const expenditure = spendByBucket[b.key] || 0;
        const profit = revenue - expenditure;
        return {
          ...b,
          revenue,
          expenditure,
          profit,
          margin: revenue > 0 ? (profit / revenue) * 100 : 0,
        };
      }),
    [buckets, invoices, spendByBucket]
  );

  const latest = rows[rows.length - 1];

  return (
    <div className="space-y-4 mb-6">
      <SegmentedControl options={GRANULARITIES} value={granularity} onChange={setGranularity} />

      {loading ? (
        <Card className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </Card>
      ) : (
        <>
          {/* Hero: the current period's bottom line, spelled out */}
          {latest && (
            <Card
              className="px-6 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              style={{
                background: latest.profit >= 0 ? '#F2FCF6' : '#FEF5F5',
                border: `1px solid ${latest.profit >= 0 ? '#C7EFD6' : '#FBD5D5'}`,
              }}
            >
              <div>
                <Mono className="text-[11px]" style={{ color: latest.profit >= 0 ? '#15803D' : '#B91C1C' }}>
                  {latest.label}
                </Mono>
                <Mono
                  className="block text-[30px] font-semibold leading-tight mt-1"
                  style={{ color: latest.profit >= 0 ? C.emerald : C.rose }}
                >
                  {fmtINR(latest.profit)}
                </Mono>
                <Mono className="block text-[11px] mt-1.5" style={{ color: C.textMuted }}>
                  {fmtINR(latest.revenue)} billed − {fmtINR(latest.expenditure)} spent = {fmtINR(latest.profit)} profit
                </Mono>
              </div>
              <Mono className="text-[12px] font-medium" style={{ color: C.textMuted }}>
                {latest.margin.toFixed(1)}% margin
              </Mono>
            </Card>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard label={`Revenue (${latest?.label || '—'})`} value={fmtINR(latest?.revenue || 0)} />
            <StatCard label="Expenditure" value={fmtINR(latest?.expenditure || 0)} />
            <StatCard
              label="Net profit"
              value={fmtINR(latest?.profit || 0)}
              delta={latest ? `${latest.margin.toFixed(1)}% margin` : null}
              deltaTone={(latest?.profit || 0) >= 0 ? 'up' : 'down'}
            />
          </div>

          <SectionCard title="Revenue vs. expenditure vs. profit" bodyClassName="p-5">
            <GroupedBars
              groups={rows.map((r) => ({
                label: r.short,
                revenue: r.revenue,
                expenditure: r.expenditure,
                profit: Math.max(r.profit, 0),
              }))}
              series={[
                { key: 'revenue', label: 'Revenue', color: C.emerald },
                { key: 'expenditure', label: 'Expenditure', color: C.amber },
                { key: 'profit', label: 'Profit', color: C.signal },
              ]}
            />
          </SectionCard>

          <Card className="overflow-hidden">
            <Table minWidth={640}>
              <thead>
                <tr>
                  <TH>Period</TH>
                  <TH align="right">Revenue</TH>
                  <TH align="right">Expenditure</TH>
                  <TH align="right">Profit</TH>
                  <TH align="right">Margin</TH>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="hover:bg-slate-50">
                    <TD>{r.label}</TD>
                    <TD align="right">
                      <Mono style={{ color: C.emerald }}>{fmtINR(r.revenue)}</Mono>
                    </TD>
                    <TD align="right">
                      <Mono style={{ color: C.amber }}>{fmtINR(r.expenditure)}</Mono>
                    </TD>
                    <TD align="right">
                      <Mono className="font-semibold" style={{ color: r.profit >= 0 ? C.ink : C.rose }}>
                        {fmtINR(r.profit)}
                      </Mono>
                    </TD>
                    <TD align="right">
                      <Mono style={{ color: C.textMuted }}>{r.margin.toFixed(1)}%</Mono>
                    </TD>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
};

export default ProfitSummary;
